import {useInfiniteQuery} from '@tanstack/react-query'
import type {InfiniteData, QueryClient} from '@tanstack/react-query'
import type {IInitiativeActivityPage} from '../types/initiativeActivityTypes'
import {apiClient} from '../restClient'
import {
  ACTIVITY_PAGE_SIZE,
  ACTIVITY_QUERY_KEY_ROOT,
  ACTIVITY_STALE_TIME_MS,
} from '../constants/initiativeActivityConstants'
import {INITIATIVE_ACTIVITY_ENDPOINT} from './endPoints'

type TActivityCache = InfiniteData<IInitiativeActivityPage, string | null>

// Sending `limit` puts the endpoint in paged mode: the whole history, newest
// first. `tzOffset` is not sent; the client formats `createdAt` itself.
const fetchActivityPage = (
  initiativeId: string,
  cursor: string | null,
): Promise<IInitiativeActivityPage> =>
  apiClient
    .get(INITIATIVE_ACTIVITY_ENDPOINT(initiativeId), {
      params: {limit: ACTIVITY_PAGE_SIZE, ...(cursor ? {cursor} : {})},
    })
    .then(r => ({
      items: r.data?.items ?? [],
      // A backend without paging returns no nextCursor: one page, no paging.
      nextCursor: r.data?.nextCursor ?? null,
    }))

export const initiativeActivityApi = {
  useInitiativeActivity: (initiativeId: string | undefined) =>
    useInfiniteQuery({
      queryKey: [ACTIVITY_QUERY_KEY_ROOT, initiativeId],
      queryFn: ({pageParam}) =>
        fetchActivityPage(initiativeId as string, pageParam),
      initialPageParam: null as string | null,
      getNextPageParam: last => last.nextCursor ?? undefined,
      enabled: !!initiativeId,
      staleTime: ACTIVITY_STALE_TIME_MS,
      // The global default is off; the feed opts in so it catches up on
      // changes made in another tab.
      refetchOnWindowFocus: true,
    }),

  /**
   * Drops every page but the first, then refetches: one request, with the
   * newest event on top. A plain invalidate would refetch every loaded page in
   * turn. Keyed on the root because task and comment mutations do not always
   * know the initiative; only a mounted feed refetches, the rest go stale.
   */
  refresh: (qc: QueryClient): Promise<void> => {
    qc.setQueriesData<TActivityCache>(
      {queryKey: [ACTIVITY_QUERY_KEY_ROOT]},
      data =>
        data
          ? {
              pages: data.pages.slice(0, 1),
              pageParams: data.pageParams.slice(0, 1),
            }
          : data,
    )
    return qc.invalidateQueries({queryKey: [ACTIVITY_QUERY_KEY_ROOT]})
  },
} as const
